import { useState, useEffect } from "react";
import {
  getCachedCustomerAvatarUrl,
  getCustomerAvatarCacheRevision,
  subscribeCustomerAvatarCache,
} from "../utils/customerAvatarCache";
import { fetchCustomerAvatarImageUrl } from "../utils/customerAvatarLoader";

/**
 * Subscribe to the shared customer avatar URL cache for a single customer.
 * Uses session/memory cache first; re-syncs when refreshCustomerAvatarCache() runs.
 */
export function useCustomerAvatarUrl(customerId, { enabled = true } = {}) {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [missing, setMissing] = useState(false);
  const [revision, setRevision] = useState(() => getCustomerAvatarCacheRevision(customerId));

  useEffect(() => {
    if (!customerId) return undefined;
    return subscribeCustomerAvatarCache((changedId) => {
      if (changedId === customerId) {
        setRevision(getCustomerAvatarCacheRevision(customerId));
      }
    });
  }, [customerId]);

  useEffect(() => {
    if (!enabled || !customerId) {
      setImageUrl(null);
      setLoading(false);
      setMissing(false);
      return undefined;
    }

    let cancelled = false;
    const cached = getCachedCustomerAvatarUrl(customerId);
    if (cached !== undefined) {
      setImageUrl(cached);
      setMissing(cached === null);
      setLoading(false);
      if (cached !== null) {
        return undefined;
      }
    } else {
      setImageUrl(null);
      setMissing(false);
      setLoading(true);
    }

    fetchCustomerAvatarImageUrl(customerId)
      .then((url) => {
        if (cancelled) return;
        setImageUrl(url);
        setMissing(!url);
      })
      .catch(() => {
        if (cancelled) return;
        setMissing(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [customerId, enabled, revision]);

  return { imageUrl, loading, missing, hasAvatar: Boolean(imageUrl) };
}
